<template>
    <TransitionRoot appear :show="open" as="template">
        <Dialog as="div" @close="closeDialog()" class="relative z-20" :initialFocus="deleteButtonRef">
            <TransitionChild
                as="template"
                enter="duration-300 ease-out"
                enter-from="opacity-0"
                enter-to="opacity-100"
                leave="duration-200 ease-in"
                leave-from="opacity-100"
                leave-to="opacity-0"
            >
                <div class="fixed inset-0 bg-black bg-opacity-25" />
            </TransitionChild>

            <div class="fixed inset-0 overflow-y-auto">
                <div
                    class="flex min-h-full items-center justify-center p-4 text-center"
                >
                    <TransitionChild
                        as="template"
                        enter="duration-300 ease-out"
                        enter-from="opacity-0 scale-95"
                        enter-to="opacity-100 scale-100"
                        leave="duration-200 ease-in"
                        leave-from="opacity-100 scale-100"
                        leave-to="opacity-0 scale-95"
                    >
                        <div
                            class="w-full max-w-md transform overflow-hidden rounded-2xl bg-white p-6 text-left align-middle shadow-xl transition-all"
                        >
                            <DialogTitle
                                as="h3"
                                class="text-lg font-medium leading-6 text-red-900"
                            >
                                Deleting channel
                            </DialogTitle>
                            <div class="mt-2">
                                <p class="text-sm text-gray-500">
                                    This is irreversible. Sure you want to delete this channel?
                                </p>
                                <div v-if="this.error">
                                    <span class="mt-2 text-sm text-red-500">{{ this.error?.message ?? "Something isn't right. Try again later?"}}</span>
                                    <div v-if="this.error?.orders" class="mt-2 flex flex-col">
                                        <h2 class="font-bold text-sm">Order(s) in question</h2>
                                        <div v-for="order in this.error.orders" class="flex flex-row p-2 mt-2 border border-green-500 rounded-lg">
                                            <span class="flex flex-grow">{{ order.reference }}</span>
                                            <a target="_blank" class="text-sm text-blue-500 flex flex-row items-center"
                                               :href="$route('orders.show', {reference: order.reference})">
                                                <span>Open</span>
                                                <ExternalLinkIcon class="h-5 w-5 ml-2"/>
                                            </a>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div class="mt-4 flex flex-row">
                                <span class="flex-grow"></span>
                                <button
                                    type="button"
                                    class="inline-flex justify-center rounded-md border border-transparent px-4 py-2 text-sm font-medium"
                                    @click="closeDialog()"
                                >
                                    No
                                </button>
                                <button
                                    type="button"
                                    ref="deleteButtonRef"
                                    class="inline-flex justify-center rounded-md border border-transparent bg-red-100 px-4 py-2 text-sm font-medium text-red-900 hover:bg-red-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-red-500 focus-visible:ring-offset-2"
                                    @click="deleteChannel()"
                                >
                                    Yes
                                </button>
                            </div>
                        </div>
                    </TransitionChild>
                </div>
            </div>
        </Dialog>
    </TransitionRoot>
</template>

<script>
import {
    Dialog,
    DialogTitle,
    TransitionRoot,
    TransitionChild,
} from "@headlessui/vue";
import axios from 'axios';
import { ref } from "vue";
import LoadingButton from "@/Shared/LoadingButton";
import {ExternalLinkIcon} from "@heroicons/vue/solid";

export default {
    emits: ['deleted', 'update:open'],
    props: {
        customer: {
            type: Object,
            required: true,
        },
        open: {
            type: Boolean,
            default: false
        },
        channel: {
            type: Object,
            required: true,
        },
    },
    setup(props) {
        const processing = ref(false);
        const deleteButtonRef = ref(null)

        return {
            processing,
            deleteButtonRef,
        }
    },
    data() {
        return {
            error: null
        }
    },
    components: {
        Dialog,
        DialogTitle,
        LoadingButton,
        TransitionRoot,
        TransitionChild,
        ExternalLinkIcon,
    },
    methods: {
        async deleteChannel() {
            this.error = null;
            this.processing = true
            try {
                const response = await axios.delete(this.$route('customers.channels.destroy', {
                    channel: this.channel.id,
                    customer: this.customer.id,
                    value: this.channel.value,
                }));
                this.$emit('deleted', response);
                this.closeDialog();
            } catch (error) {
                if (error.response && error.response.data) {
                    this.error = error.response.data;
                }
            }
            this.processing = false
        },
        closeDialog() {
            this.$emit('update:open', false)
        }
    }
}
</script>
